import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/common/DashboardLayout';
import PageHeading from '../../components/common/PageHeading';
import * as memberRequestService from '../../services/memberRequest.service';

const CATEGORIES = [
  { value: 'event_request',  label: 'A live event you\'d like to see' },
  { value: 'tech_issue',     label: 'Something\'s not working right' },
  { value: 'course_request', label: 'Course content you\'d like to see' },
  { value: 'other',          label: 'Something else' },
];

const STATUS_LABELS = {
  new: 'Received', in_review: 'Being looked at', scheduled: 'Scheduled',
  resolved: 'Resolved', declined: 'Not this time',
};

export default function TellUs() {
  const [category, setCategory] = useState('event_request');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);
  const [mine, setMine] = useState(null);

  const load = useCallback(() => {
    memberRequestService.listMyRequests().then(setMine).catch(() => setMine([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await memberRequestService.createRequest({ category, subject: subject.trim() || null, body: body.trim() });
      setSubject('');
      setBody('');
      setSent(true);
      setTimeout(() => setSent(false), 4000);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not send this — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <PageHeading title="Tell Us" subtitle="Want a new kind of live event? Something not working? Course content you wish existed? Tell us here — a real person reads every one." />
        </div>

        <form onSubmit={submit} className="max-w-2xl bg-white rounded-2xl shadow-sm p-5 space-y-3 mb-8">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">What's this about?</label>
            <select
              value={category} onChange={e => setCategory(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
            >
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Title (optional)</label>
            <input
              value={subject} onChange={e => setSubject(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Tell us more</label>
            <textarea
              value={body} onChange={e => setBody(e.target.value)} rows={5} required
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
            />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          {sent && <p className="text-sm text-reset-sage">Sent — thank you. We'll get back to you here.</p>}
          <div className="flex justify-end">
            <button
              type="submit" disabled={submitting || !body.trim()}
              className="px-5 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50"
            >
              {submitting ? 'Sending…' : 'Send'}
            </button>
          </div>
        </form>

        {mine && mine.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">What you've told us</h2>
            <div className="space-y-3">
              {mine.map(r => (
                <div key={r.id} className="bg-white rounded-2xl shadow-sm p-4">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p className="text-sm font-semibold text-reset-brown">{r.subject || CATEGORIES.find(c => c.value === r.category)?.label}</p>
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-reset-cream text-reset-brown shrink-0">
                      {STATUS_LABELS[r.status] || r.status}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">{r.body}</p>
                  {r.responses?.length > 0 && (
                    <div className="border-t border-gray-50 pt-2 mt-2 space-y-2">
                      {r.responses.map(resp => (
                        <p key={resp.id} className="text-xs text-reset-sage bg-reset-cream rounded-lg p-2">{resp.body}</p>
                      ))}
                    </div>
                  )}
                  <p className="text-[10px] text-gray-400 mt-2">{new Date(r.created_at).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
